import React, { useState, useEffect, useRef, FC } from 'react';
import styled from '@emotion/styled';
import { Textfit } from 'react-textfit';

import { ItemFragmentOnItem_upsales } from '../GridButtons/__queries__';
import { FormattedModificationGroup, useI18n, useLogger } from '~Hooks';
import ModificationButton from './Button';
import { Tab } from './View';
import { getSplittedTitle } from './utils';
import { useEntryFinder } from '~Components/EntryFinder';

const Wrapper = styled.div<{
  disabled?: boolean;
  isGridView?: boolean;
}>`
  max-width: 214px;
  min-width: ${({ isGridView }) => (isGridView ? '214px' : '144px')};
  flex: 1;
  overflow: hidden;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  max-height: 100%;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

/** Red asterisk after a required group's title, same marker as HandyPOS */
const RequiredMarker = styled.strong`
  color: var(--colorStatusRed);
  margin-left: 4px;
`;

const Items = styled.div<{
  isGridView?: boolean;
}>`
  overflow-y: auto;
  padding: ${({ isGridView }) => (isGridView ? '0 8px 64px' : '0 8px 200px')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

interface Props {
  modificationGroup?: FormattedModificationGroup;
  upsell?: ItemFragmentOnItem_upsales;
  selectedUpsales?: Array<{ id: string; title: string }>;
  isGridView?: boolean;
  onSelect: (id: string, title?: string) => void;
}

const Column: FC<Props> = ({
  modificationGroup,
  upsell,
  selectedUpsales,
  isGridView,
  onSelect,
}) => {
  const { i18n } = useI18n();
  const findEntryFromItem = useEntryFinder();
  const logger = useLogger('variations/upsells');

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState<boolean>(false);

  useEffect(() => {
    if (scrollRef.current) {
      setHasScroll(scrollRef.current.scrollHeight > scrollRef.current.clientHeight);
    }
  }, [scrollRef.current]);

  const splittedString = getSplittedTitle(
    (modificationGroup && modificationGroup.title) ?? (upsell && i18n(upsell.name)) ?? '',
  );

  return (
    <Wrapper isGridView={isGridView}>
      <Tab>
        <Textfit max={16} min={12}>
          {splittedString.length == 2 && <span>{splittedString[0]}</span>}
          {splittedString[1] ?? splittedString[0]}
          {modificationGroup?.required && <RequiredMarker>*</RequiredMarker>}
        </Textfit>
      </Tab>
      <Items isGridView={isGridView} ref={scrollRef} onTouchStart={(e) => e.stopPropagation()}>
        {modificationGroup?.variations.map((variation) => {
          return (
            <ModificationButton
              key={variation.id}
              title={variation.name}
              active={variation.selected}
              isSingle={modificationGroup?.type === 'SINGLE'}
              price={variation.price}
              isOutOfStock={variation.isOutOfStock}
              onTouchStart={() => (!hasScroll ? onSelect(variation.id) : undefined)}
              onClick={() => (hasScroll ? onSelect(variation.id) : undefined)}
            />
          );
        })}
        {upsell &&
          upsell.items.map((upsellItem) => {
            const foundEntry = findEntryFromItem(upsellItem.id);

            // Don't render items that is not included in menu
            if (!foundEntry) {
              // Likely a misconfiguration - An item has an upsell that is not included in the menu
              logger.warn(
                `Upsell in POS not rendered, could not find entry for ${upsellItem.id}`,
                foundEntry,
              );
              return;
            }

            return (
              <ModificationButton
                key={upsellItem.id}
                title={i18n(upsellItem.titleLang)}
                active={
                  selectedUpsales ? !!selectedUpsales.find((i) => i.id === upsellItem.id) : false
                }
                isSingle={false}
                price={foundEntry.price}
                onTouchStart={() =>
                  !hasScroll ? onSelect(upsellItem.id, i18n(upsellItem.titleLang)) : undefined
                }
                onClick={() =>
                  hasScroll ? onSelect(upsellItem.id, i18n(upsellItem.titleLang)) : undefined
                }
              />
            );
          })}
      </Items>
    </Wrapper>
  );
};

export default Column;
